import './style.scss'
import { Button, message, Modal, Skeleton, Switch, Tooltip } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { Buffer } from 'buffer'
import dayjs from 'dayjs'
import VirtualList from 'rc-virtual-list'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import wavConverter from 'wav-converter'
import * as HomeApi from '@/api/home'
import { Play } from '@/assets/svg'
import { Back } from '@/assets/svg/back'
import { PlayGray } from '@/assets/svg/play-gray'
import PayPointModal from '@/components/PayPointModal'
import UploadVideo from '@/components/UploadVideo'
import VideoModal from '@/components/VideoModal'
import { UserStore } from '@/global-states'
import {
  checkSensitive,
  downlad,
  eventTracking,
  getRandomTexts,
  Render,
  showErrorwordsTip,
  showPlanModal,
  urlOsskey,
  urlSource
} from '@/libs/util'
import {
  DeleteOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PauseOutlined,
  SyncOutlined
} from '@ant-design/icons'
import VoiceModal from '../../create-video/components/VoiceModal'

const PicturePage: React.FC = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const { userPackage, loginStatus } = UserStore
  const params: any = useParams()
  const [searchParams] = useSearchParams()
  const id = +(params?.id as any)
  const [config, setConfig] = useState<any>(undefined)
  const [subtitleEnabled, setSubtitleEnabled] = useState(true)
  const [cartoonEnabled, setCartoonEnabled] = useState(false)
  const [paypointModalOpen, setPayPointModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [videoList, setVideoList] = useState<any>(undefined as any)
  const [pagination, setPagination] = useState({ current: 0, pageSize: 10, total: 0 })
  const [videoLoading, setVideoLoading] = useState(false)
  const [videoId, setVideoId] = useState(-1)
  const [height, setHeight] = useState(0)
  const listRef = useRef<any>(null)
  const loadingRef = useRef(false)
  const [preview, setPreview] = useState<any>()
  const [randomKey, setRandomKey] = useState<any>()
  const [activeVideoIndex, setActiveVideoIndex] = useState(-1)
  const [scriptText, setScriptText] = useState('')
  const [currentTts, setCurrentTts] = useState<any>()
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [osskey, setOsskey] = useState('')
  const [videoSize, setVideoSize] = useState<any>()
  const [itemHeight, setItemHeight] = useState(0)
  const rightRef = useRef<any>()
  const intervalRef = useRef<NodeJS.Timeout>()
  const audioRef = useRef<any>()
  const prviewRef = useRef<any>()
  const [previewId, setPreviewId] = useState<any>(undefined)
  const [playingId, setPlayingId] = useState<any>(undefined)

  useEffect(() => {
    eventTracking('PicturePage', { id })
    ;(window as any).Buffer = Buffer
    UserStore.initSensitiveWords()
    setRandomText()
    initVoice()
    setTimeout(() => {
      getConfig()
      setVideoList([])
      getVideoList()
      updateVirtualConfig()
    }, 300)
    if ((searchParams.get('type') as any) == 2) {
      setCartoonEnabled(true)
    }
    initItemHeight()
    intervalRef.current = setInterval(updateVideoStatuses, 10000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      prviewRef.current = undefined
      closeAudio()
    }
  }, [])

  useEffect(() => {
    prviewRef.current = previewId
  }, [previewId])

  useEffect(() => {
    if (loginStatus) {
      getVideoList()
    }
  }, [loginStatus])

  const updateVideoStatuses = async () => {
    try {
      const res = await HomeApi.getImageVideos({
        id,
        size: pagination.pageSize,
        page: pagination.current,
        di: UserStore.device_id
      })

      if (res?.list?.length) {
        setVideoList((prevList: any[]) => {
          return prevList.map((item) => {
            const updatedItem = res.list.find((v: any) => v.id === item.id)
            if (updatedItem && item.status === 1) {
              return updatedItem
            }
            return item
          })
        })
      }
    } catch (error) {
      console.error('Failed to update video statuses:', error)
    }
  }

  const initItemHeight = () => {
    const dom = rightRef.current
    const w = dom?.offsetWidth
    if (w) {
      setItemHeight((w - 80) * (9 / 16) + 56)
    }
  }

  const getConfig = async () => {
    const res = await HomeApi.getImageConfig({
      id
    })
    setConfig(res)
  }

  const updateVirtualConfig = () => {
    const h = window.innerHeight - 160
    setHeight(h)
  }

  const initVoice = async () => {
    const voiceId = localStorage.getItem('voiceId') || ''
    const [global, self] = await Promise.all([HomeApi.getGlobalTts(), HomeApi.getSelfTts()])
    const speakers = [...(self?.list || []), ...(global?.list || [])]?.filter((i) => i.status === 1) || []

    const level = userPackage?.current_membership_level || 0
    // 过滤掉不可用的精品声音和高保真
    const canUseSpeakers = speakers.filter((s) => {
      if (s.is_premium && level < 10) {
        return false
      }
      if (s.level === 20 && level < 20) {
        return false
      }
      return true
    })

    const findVoice = canUseSpeakers.find((s) => s.id === +voiceId)
    let voice: any
    if (voiceId && findVoice) {
      voice = findVoice
    } else {
      voice = canUseSpeakers[0]
    }

    setCurrentTts(voice)
  }

  const getVideoList = async (page = 0, pageSize = 10) => {
    if (loadingRef.current) return
    loadingRef.current = true
    try {
      setVideoLoading(true)
      const res = await HomeApi.getImageVideos({
        id,
        size: pageSize,
        page,
        di: UserStore.device_id
      })

      setVideoList(page === 0 ? res?.list || [] : [...(videoList || []), ...(res?.list || [])])
      setPagination((prev) => ({
        ...prev,
        current: page,
        total: res?.total || 0
      }))
    } catch (error: any) {
      setVideoList([])
    } finally {
      setVideoLoading(false)
      loadingRef.current = false
    }
  }

  const handleLoadMore = useCallback(() => {
    if (videoLoading || videoList?.length >= pagination.total) return
    const nextPage = pagination.current + 1
    getVideoList(nextPage, pagination.pageSize)
  }, [pagination, videoLoading])

  const handleDeleteVideo = async (item: any) => {
    Modal.confirm({
      title: translate('picture.delete_title'),
      content: translate('picture.delete_content'),
      onOk: async () => {
        await HomeApi.deleteImageVideos(item.id)
        setVideoList(videoList?.filter((p: any) => p.id !== item.id))
        message.success(translate('picture.delete_success'))
      }
    })
  }

  const playVideo = (item: any) => {
    setVideoId(item.id)
  }

  const onScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      handleLoadMore()
    }
  }

  const handleGenerate = async () => {
    if (!scriptText.trim()) {
      return message.warning(translate('picture.empty_script_warning'))
    }

    if (!osskey) {
      return message.warning(translate('picture.empty_image_warning'))
    }

    const errorWords = checkSensitive([{ words: scriptText }])

    if (errorWords?.length) {
      return showErrorwordsTip(errorWords)
    }
    submit()
  }

  const submit = async () => {
    let width = videoSize.w
    let height = videoSize.h
    const fixed_video_size = config?.fixed_video_size

    if (fixed_video_size) {
      const ratio = width / height
      const f_w = fixed_video_size.width
      const f_h = fixed_video_size.height
      width = ratio > 1 ? f_h : f_w
      height = ratio > 1 ? f_w : f_h
    }

    const fontSize = 80
    const titleHeight = fontSize * 1.5 * (Math.max(width, height) / 1920)

    const params: any = {
      id,
      image_oss_key: osskey,
      voice_name: currentTts?.name,
      video_width: width,
      video_height: height,
      text: scriptText,
      di: UserStore.device_id,
      style: cartoonEnabled ? 2 : 1
    }

    if (subtitleEnabled) {
      params.subtitle = {
        font_name: '抖音美好体',
        font_size: fontSize,
        primary_color: '#ffffff',
        outline_color: '#000000',
        width: Math.floor(width * 0.8),
        height: Math.floor(titleHeight),
        pos_x: Math.floor(width * 0.1),
        pos_y: Math.floor(height * 0.85)
      }
    }

    try {
      setLoading(true)
      const res = await HomeApi.submitImageVideos(params)

      if (res.sensitive_words) {
        showErrorwordsTip(res.sensitive_words)
      } else {
        listRef.current?.scrollTo(0)
        message.success(translate('picture.submit_success'))
      }

      setPagination({ current: 0, pageSize: 10, total: 0 })
      getVideoList(0, pagination.pageSize)
    } catch (error: any) {
      if (error.code === 1024) {
        message.error(error.message)
      }
      if (error.code === 1002) {
        setPayPointModalOpen(true)
      }
      if (error.code === 1003 || error.code === 1004 || error.code === 1005) {
        showPlanModal()
      }
    } finally {
      setLoading(false)
    }
  }

  const onselectExample = (example: any, index: number) => {
    setOsskey(example.oss_key)
    setActiveVideoIndex(index)
  }

  const setRandomText = () => {
    const r: any = getRandomTexts().filter((t) => t.key !== randomKey)
    const index = Math.floor(Math.random() * r.length)
    setRandomKey(r[index].key)
    setScriptText(r[index].text)
  }

  const onVoiceChange = (voice: any) => {
    setCurrentTts(voice)
  }

  const clearAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause?.()
      audioRef.current.src = ''
    }
  }

  const closeAudio = () => {
    clearAudio()
    setPlayingId(undefined)
    setPreviewId(undefined)
  }

  const previewTts = async (tts: any, text: string) => {
    if (playingId === tts?.name) {
      closeAudio()
      return
    } else if (previewId === tts.id) {
      return
    }

    setPreviewId(tts.name)
    setPlayingId(undefined)
    clearAudio()

    try {
      let res
      if (tts.parent_id) {
        res = await HomeApi.previewCommunityVoice(tts.parent_id, tts.id, {
          text,
          voice_parameters: tts.voice_parameters
        })
      } else {
        res = await HomeApi.previewTts(tts.id, { text, voice_parameters: tts.voice_parameters })
      }
      if (prviewRef.current !== tts.name) {
        return
      }
      setPlayingId(tts.name)

      clearAudio()

      const audio = new Audio()
      audio.src = `data:audio/wav;base64,${wavConverter
        .encodeWav(new Buffer(res.audio_base64, 'base64'), {
          numChannels: 1,
          sampleRate: 16000,
          byteRate: 32_000
        })
        .toString('base64')}`
      audio.play()
      audioRef.current = audio

      audio.addEventListener('ended', () => {
        closeAudio()
      })

      audio.addEventListener('pause', function () {
        closeAudio()
      })
    } catch {
      closeAudio()
    }
  }

  return (
    <div className="page-common page-picture">
      <div className="common-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <Back />
        </button>
        <span>{translate('picture.page_title')}</span>
      </div>
      <div className="common-wrapper">
        <main className="page-content">
          <div className="main-container">
            <div className="controls-panel">
              <div className="panel-content">
                <div>
                  <div className="card-header">
                    <h2>{translate('picture.upload_image')}</h2>
                  </div>
                  <div className="card">
                    {activeVideoIndex > -1 ? (
                      <div className="step-view-box video">
                        <img
                          src={urlOsskey(osskey)}
                          onLoad={(e: any) => {
                            setVideoSize({
                              w: e.target.naturalWidth,
                              h: e.target.naturalHeight
                            })
                          }}
                        />
                        <div
                          className="trash"
                          onClick={() => {
                            setOsskey('')
                            setStep(0)
                            setActiveVideoIndex(-1)
                          }}
                        >
                          <DeleteOutlined />
                        </div>
                      </div>
                    ) : (
                      <UploadVideo
                        uploadContent={
                          <>
                            <p className="ant-upload-text">{translate('picture.upload_click_drag')}</p>
                            <p className="ant-upload-hint" style={{ fontSize: '13px' }}>
                              {translate('picture.upload_support')}
                            </p>
                          </>
                        }
                        onUploadSuccess={(video: any) => {
                          setVideoSize({
                            w: video?.videoWidth,
                            h: video?.videoHeight
                          })
                          setOsskey(video.oss_key)
                        }}
                        onRestartUpload={() => {
                          setOsskey('')
                        }}
                        upateStep={(step) => setStep(step)}
                        format={'png、jpg、jpeg'}
                        size={10}
                        mediaType={3}
                      />
                    )}

                    {step === 0 && !osskey && (
                      <div className="uploader-footer">
                        <span className="uploader-footer-label">{translate('picture.recommend')}:</span>
                        <div className="example-grid-compact">
                          {config?.recommend_images?.map((example: any, index: number) => (
                            <div
                              key={example.id}
                              className={`example-item-compact ${activeVideoIndex === index ? 'active' : ''}`}
                              onClick={() => onselectExample(example, index)}
                            >
                              <img src={urlSource(example.url, 'video')} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* <div className="footer-switch">
                      <span>{translate('picture.cartoon_style')}：</span>
                      <Switch checked={!!cartoonEnabled} onChange={(v) => setCartoonEnabled(v)} />
                    </div> */}
                  </div>
                </div>
                <div>
                  <div className="card-header">
                    <h2>{translate('picture.voice')}</h2>
                  </div>
                  <div className="card voice-selection">
                    <div className="voice-info">
                      <div className="img-box">
                        <img src={currentTts?.group_cover_url} alt="Voice avatar" />
                      </div>

                      <div
                        className="play-pause"
                        onClick={(e) => {
                          e.stopPropagation()
                          previewTts(currentTts, currentTts?.preview_text || translate('systm.default_preview_text'))
                        }}
                      >
                        {previewId === currentTts?.name ? (
                          playingId === currentTts?.name ? (
                            <PauseOutlined />
                          ) : (
                            <LoadingOutlined />
                          )
                        ) : (
                          <PlayGray className="play-icon" />
                        )}
                      </div>

                      <div className="voice-details">
                        <p className="voice-name">{currentTts?.group_title}</p>
                        <p className="voice-style">{currentTts?.display_name}</p>
                      </div>
                    </div>
                    <Button
                      className="gray-btn"
                      type="default"
                      onClick={() => {
                        setVoiceModalOpen(true)
                        closeAudio()
                      }}
                    >
                      {translate('picture.change_voice')}
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="card-header">
                    <h2>{translate('picture.script')}</h2>
                    <Button className="gray-btn" type="default" icon={<SyncOutlined />} onClick={setRandomText}>
                      {translate('picture.random')}
                    </Button>
                  </div>
                  <div className="card">
                    <TextArea
                      className="script-textarea"
                      placeholder={translate('picture.script_placeholder')}
                      value={scriptText}
                      onChange={(e) => setScriptText(e.target.value)}
                      maxLength={10000}
                    ></TextArea>
                  </div>
                </div>

                <div>
                  <div className="card-header">
                    <h2>{translate('picture.subtitle')}</h2>
                    <Switch checked={!!subtitleEnabled} onChange={(v) => setSubtitleEnabled(v)} />
                  </div>
                </div>
              </div>
              <div className="action-footer">
                <p className="cost-breakdown">
                  <Tooltip title={translate('picture.digital_human_tip')}>
                    {translate('picture.digital_human_cost')}
                    {(cartoonEnabled ? config?.credit_cost?.cartoon : config?.credit_cost?.realistic) || '--'}
                    {translate('picture.points')}
                    <span className="tooltip-container">
                      <i className="info-icon"> i</i>
                    </span>
                  </Tooltip>
                  <span className="add">+</span>
                  <Tooltip
                    color="#fff"
                    title={
                      <a
                        target="_blank"
                        href="https://lingverse.feishu.cn/sheets/IJeQsahvdhqX5btBSzycBKPRnr0"
                        rel="noreferrer"
                      >
                        {translate('picture.view_points_explanation')}
                      </a>
                    }
                  >
                    {translate('picture.video_creation_cost')}
                    <span className="tooltip-container">
                      <i className="info-icon"> i</i>
                    </span>
                  </Tooltip>
                </p>

                <Button loading={loading} className="footer-btn" type="primary" onClick={handleGenerate}>
                  ✨ {translate('picture.submit')}
                </Button>
              </div>
            </div>
            <div className="main-right" ref={rightRef}>
              {!videoList || !config || !itemHeight ? (
                <div className="skeleton">
                  <Skeleton active />
                </div>
              ) : (
                <>
                  {videoList?.length === 0 ? (
                    <div className="right-title">
                      <span>{translate('picture.example_effect')}</span>
                    </div>
                  ) : (
                    <div className="right-title">
                      <span>{translate('picture.latest_works')}</span>
                      <span
                        className="link gradient"
                        onClick={() => {
                          setPreview({
                            url: config?.demo_video_urls?.[0],
                            title: translate('picture.example_effect')
                          })
                        }}
                      >
                        {translate('picture.example_effect')}
                      </span>
                    </div>
                  )}

                  <div className="video-list-container">
                    {videoList?.length === 0 && config?.demo_video_urls ? (
                      <div className="video-box">
                        <video className="video" src={config?.demo_video_urls?.[0]} controls autoPlay muted />
                      </div>
                    ) : (
                      <VirtualList
                        data={videoList || []}
                        height={height}
                        itemHeight={itemHeight}
                        itemKey="id"
                        onScroll={onScroll}
                        ref={listRef}
                      >
                        {(item: any) => (
                          <div className="video-item" key={item.id} data-id={item.id}>
                            <Render>
                              {function List() {
                                const [percent, setPercent] = useState(-1)
                                const xhrRef = useRef<any>()
                                const downladVideo = (d: any) => {
                                  if (xhrRef.current) {
                                    xhrRef.current.abort()
                                    setPercent(0)
                                  }
                                  const url = d.url.split('?')[0]
                                  const extension = url.substring(url.lastIndexOf('.')).toLowerCase()
                                  const xhr: XMLHttpRequest = downlad(
                                    d.url,
                                    `${d.title || translate('picture.unnamed')}${extension}`
                                  )
                                  xhr.onprogress = (e) => {
                                    const p = Math.floor((e.loaded / e.total) * 100)
                                    setPercent(p)
                                    if (p === 100) {
                                      setTimeout(() => {
                                        setPercent(-1)
                                      }, 300)
                                    }
                                  }
                                  xhrRef.current = xhr
                                }

                                return (
                                  <>
                                    <div className="video-preview">
                                      {item.id === videoId ? (
                                        <video src={item.url} poster={item.preview_url} controls autoPlay />
                                      ) : (
                                        <img src={item.preview_url} alt="Preview" />
                                      )}

                                      {item.status === 1 && (
                                        <div className="mask">
                                          <div className="pending">
                                            <LoadingOutlined />
                                            <div>{translate('picture.generating')}</div>
                                          </div>
                                        </div>
                                      )}

                                      {item.status === 2 && item.id !== videoId && (
                                        <div className="play" onClick={() => playVideo(item)}>
                                          <Play />
                                        </div>
                                      )}

                                      {item.status === 3 && (
                                        <div className="mask">
                                          <Tooltip placement="bottom" title={item.error_message || ''}>
                                            <span className="status error">
                                              {translate('picture.generation_failed')}{' '}
                                              {item.error_message && <InfoCircleOutlined />}
                                            </span>
                                          </Tooltip>
                                        </div>
                                      )}

                                      {percent > -1 && (
                                        <div className="mask">
                                          <div className="pending">
                                            <LoadingOutlined />
                                            <div>
                                              {translate('picture.downloading')} {percent}%
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                    <div className="video-info">
                                      <span className="create-time">
                                        {dayjs.unix(item.create_time).format('YYYY-MM-DD HH:mm:ss')}
                                      </span>
                                      {item.status !== 1 && (
                                        <Tooltip placement="top" title={translate('picture.delete')}>
                                          <Button
                                            className="download"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDeleteVideo(item)}
                                          ></Button>
                                        </Tooltip>
                                      )}

                                      {item.status === 2 && (
                                        <Tooltip placement="top" title={translate('picture.download')}>
                                          <Button
                                            className="download"
                                            icon={<DownloadOutlined />}
                                            onClick={() => downladVideo(item)}
                                          ></Button>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </>
                                )
                              }}
                            </Render>
                          </div>
                        )}
                      </VirtualList>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      <VoiceModal
        open={voiceModalOpen}
        selectTts={currentTts}
        onCancel={() => setVoiceModalOpen(false)}
        upgradePlan={showPlanModal}
        onSelect={onVoiceChange}
      />

      <PayPointModal open={paypointModalOpen} onCancel={() => setPayPointModalOpen(false)} />

      <VideoModal preview={preview} onCancel={() => setPreview(undefined)} />
    </div>
  )
}

export default PicturePage
